// Invoice DAO — data access for invoices, line items, links, and related views.
// Owns all query complexity. Each method = one Supabase call.

import { BaseDao, type SupabaseClient, type InvalidateFn } from '../base.ts';
import type {
  InvoiceRow,
  InvoiceLineRow,
  InvoiceLinkRow,
  InvoiceBankAccountRow,
  InvoiceEntityView,
  InvoiceLink,
  InvoiceLineInsert,
  InvoiceLinkInsert,
  Invoice,
  InvoiceSummary,
} from '@shared/types/invoice.ts';
import type { TransactionSideType } from './InvoiceReconcileService.ts';
import { enrichInvoiceLink } from '../../transforms/invoice.ts';
import { monthOf } from '../../calc.ts';
import type { PriceType, InvoiceDirection } from '../../types.ts';
import { ibanCore } from '../../utils/iban.ts';
import type { WarningClause } from './warningClauses.ts';
import { withPendingMarker, withoutPendingMarker } from './holdMarker.ts';
import type { ReceivedHoldCheckRow } from './holdPolicy.ts';

const WEIGHT_UNITS = new Set(['t', 't.', 'Mg', 'mg', 'kg', 'tona', 'ton', 'tonne']);

/** Payload for the atomic `reconcile_apply` SQL function. */
export interface ReconcileWriteLink {
  invoice_line_item_id: number | null;
  transaction_id: number | null;
  transaction_transport_id: number | null;
  side: 'load' | 'unload' | null;
  stale: boolean;
}
export interface ReconcileWriteInvoice {
  id: number;
  review_status: 'approved' | 'pending' | 'request_correction';
  report: unknown;
  links: ReconcileWriteLink[];
  /** Finding 7: invoices that entered the sweep pool ALREADY approved (arrived
   *  approved from Fakturownia, never reconciled). reconcile_apply then writes links +
   *  report but LEAVES review_status untouched — matching must never demote an approved
   *  invoice. Omitted/false = write the matcher's decision as normal. */
  preserve_review_status?: boolean;
}
export interface ReconcileWritePayload {
  invoices: ReconcileWriteInvoice[];
  approved_correction_ids: number[];
}

/** Summary returned by the atomic `reconcile_apply` SQL function (finding 4): how many
 *  auto-link inserts the freshness guard dropped, per invoice, so a silently dropped link
 *  is surfaced (logged + Sentry when > 0). */
export interface ReconcileApplySummary {
  guard_skipped_total: number;
  invoices: { id: number; guard_skipped: number }[];
}

/** Columns the outbound push needs, shared by every way of selecting its
 *  candidates (dirty flag, approval drift, PENDING-note drift) so the three
 *  paths cannot drift apart in shape. `currency` / `fakturownia_department_id` /
 *  `buyer_bank_account` / `seller_company_id` feed department/bank
 *  account routing (resolveFakturowniaRouting) — the loop needs the invoice's
 *  currency and vendor to resolve where it should route, and the mirror
 *  columns to detect drift against what Fakturownia already holds. */
const OUTBOUND_INVOICE_COLUMNS =
  'id, external_system_id, invoice_number, payment_status, notes, updated_at, review_status, fakturownia_approval_status, currency, fakturownia_department_id, buyer_bank_account, seller_company_id';

export type OutboundInvoiceRow = Pick<
  InvoiceRow,
  'id' | 'external_system_id' | 'invoice_number' | 'payment_status' | 'notes' | 'updated_at' | 'review_status' | 'fakturownia_approval_status'
  | 'currency' | 'fakturownia_department_id' | 'buyer_bank_account' | 'seller_company_id'
>;

/** Derive price_type from unit stored in DB. */
function inferPriceType(unit: string | null | undefined): PriceType {
  return unit && WEIGHT_UNITS.has(unit) ? 'per_ton' : 'flat_rate';
}

function enrichLineItem(row: InvoiceLineRow): InvoiceLineRow {
  if (!row.price_type) row.price_type = inferPriceType(row.unit);
  return row;
}

// ── InvoiceDao ──────────────────────────────────────────────────────────

export class InvoiceDao extends BaseDao<InvoiceRow> {
  protected readonly dependentResources = [
    'invoices',
    'invoice_line_items',
    'invoice_links',
    'invoice_bank_accounts',
    'invoice_resolved_links',
    'transactions_with_checks',
    'reconcile_candidate_transactions',
  ];

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    super(sb, 'invoices', invalidate);
  }

  // --- Invoice queries ---

  /** Fetch invoice with company joins, correction parent, and child corrections. */
  async getWithCompanies(invoiceId: number): Promise<Invoice['invoice'] & {
    corrections?: InvoiceSummary[];
  }> {
    const res = await this.sb
      .from('invoices')
      .select(`*,
        seller_company:seller_company_id(id, short_name, full_name, nip, country_code, street, zip_code, city),
        buyer_company:buyer_company_id(id, short_name, full_name, nip, country_code, street, zip_code, city),
        recipient_company:recipient_company_id(id, short_name),
        correction_of_invoice:correction_of_invoice_id(id, invoice_number),
        corrections:invoices!correction_of_invoice_id(id, invoice_number, invoice_type, issue_date, sell_date, issue_place, notes, net_amount, vat_amount, gross_amount, currency, is_deleted)`)
      .eq('id', invoiceId)
      .single();
    if (res.error) throw res.error;
    return res.data as unknown as Invoice['invoice'] & { corrections?: InvoiceSummary[] };
  }

  async getWithLineItems(invoiceId: number): Promise<InvoiceRow & { invoice_line_items: InvoiceLineRow[] }> {
    const res = await this.sb
      .from('invoices')
      .select('*, invoice_line_items(*)')
      .eq('id', invoiceId)
      .single();
    if (res.error) throw res.error;
    return res.data as InvoiceRow & { invoice_line_items: InvoiceLineRow[] };
  }

  // --- Pending invoice queries (for reconciliation) ---

  /** Fetch pending/partial invoices not recently touched, optionally filtered by type and excluding given cost categories. */
  async getPendingInvoices(
    direction: InvoiceDirection,
    opts?: { limit?: number; invoiceId?: number; invoiceType?: string; touchTimeoutMinutes?: number; ignoreTouch?: boolean; includeApprovedUnswept?: boolean; includeRequestCorrection?: boolean },
  ): Promise<InvoiceRow[]> {
    const limit = opts?.limit ?? 50;
    const timeoutMinutes = opts?.touchTimeoutMinutes ?? 2;
    const touchCutoff = new Date(Date.now() - timeoutMinutes * 60_000).toISOString();

    // PAGINATED: the full sweep asks for the whole pending pool in one call (limit far
    // above PostgREST's 1000-row cap). A hard .limit() silently truncated at 1000, so the
    // single-batch global match only saw the first 1000 invoices. Page through .range()
    // until a short page (or `limit` reached) so one call returns the entire pool.
    const pageSize = Math.min(limit, 1000);
    const rows: InvoiceRow[] = [];
    for (let from = 0; rows.length < limit; from += pageSize) {
      let q = this.sb
        .from('invoices')
        .select('*')
        .eq('is_deleted', false)
        .eq('direction', direction);
      // Coverage (finding 7): the sweep normally pulls only review_status='pending'
      // invoices, so invoices that arrived ALREADY approved from Fakturownia (never
      // reconciled) were never swept (728 on dev = 92% of the reconcile gap). When
      // includeApprovedUnswept is set, additionally pull approved-but-never-swept
      // originals (review_status='approved' AND reconcile_report IS NULL). The caller
      // marks these preserve_review_status so matching writes links + report without
      // demoting their approved status.
      // includeRequestCorrection: an invoice flagged for a vendor correction is otherwise
      // unreachable — no sweep selects it, so when the correction finally arrives nothing
      // recomputes the parent and the flag is permanent. Re-selecting it is idempotent:
      // with no new correction, matching yields the identical result and no state change.
      // Built as ONE .or() string because PostgREST ANDs successive .or() calls.
      const statusPredicates = ['review_status.eq.pending'];
      if (opts?.includeRequestCorrection) statusPredicates.push('review_status.eq.request_correction');
      if (opts?.includeApprovedUnswept) {
        statusPredicates.push('and(review_status.eq.approved,reconcile_report.is.null,invoice_type.eq.original)');
      }
      if (statusPredicates.length > 1) {
        q = q.or(statusPredicates.join(','));
      } else {
        q = q.in('review_status', ['pending']);
      }
      // Touch filter coordinates the chunked/looped mode only. The full sweep holds the
      // reconcile lock for the whole single pass, so it ignores touch entirely.
      if (!opts?.ignoreTouch) {
        q = q.or(`reconcile_touched_at.is.null,reconcile_touched_at.lt.${touchCutoff}`);
      }
      if (opts?.invoiceType) q = q.eq('invoice_type', opts.invoiceType);
      if (opts?.invoiceId != null) q = q.eq('id', opts.invoiceId);
      q = q.order('id', { ascending: true }).range(from, from + pageSize - 1);
      const { data, error } = await q;
      if (error) throw error;
      const page = (data as InvoiceRow[]) ?? [];
      rows.push(...page);
      if (page.length < pageSize) break;
    }
    return rows.length > limit ? rows.slice(0, limit) : rows;
  }

  /** Mark invoices as being processed (set reconcile_touched_at = now). */
  async touchInvoices(ids: number[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await this.sb
      .from('invoices')
      .update({ reconcile_touched_at: new Date().toISOString() } as Record<string, unknown>)
      .in('id', ids);
    if (error) throw error;
  }

  /** Clear touch so invoices are eligible for the next reconcile run. */
  async clearTouch(ids: number[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await this.sb
      .from('invoices')
      .update({ reconcile_touched_at: null } as Record<string, unknown>)
      .in('id', ids);
    if (error) throw error;
  }

  /** Fetch correction invoices for given original IDs. */
  async getCorrections(originalIds: number[]): Promise<InvoiceRow[]> {
    if (originalIds.length === 0) return [];
    const { data, error } = await this.sb
      .from('invoices')
      .select('*')
      .in('correction_of_invoice_id', originalIds)
      .eq('is_deleted', false);
    if (error) throw error;
    return (data as InvoiceRow[]) ?? [];
  }

  /**
   * Per-invoice gross magnitudes folded with their non-deleted corrections'
   * signed gross deltas, plus the correction-id → parent-id map (for summing
   * payment links across the family). Returns:
   *   - grossById:        raw gross_amount per requested invoice id
   *   - corrDeltaByParent: Σ non-deleted corrections' gross_amount per parent id
   *   - corrIdToParent:    each correction id → its parent id
   *
   * Extracted (finding MINOR-8) so callers computing invoice outstanding stop
   * hand-writing these `invoices` queries. Filters match the prior inline SQL
   * exactly: corrections are invoice_type='correction' AND is_deleted=false.
   */
  async getGrossWithCorrectionDeltas(invoiceIds: number[]): Promise<{
    grossById: Map<number, number>;
    corrDeltaByParent: Map<number, number>;
    corrIdToParent: Map<number, number>;
  }> {
    const grossById = new Map<number, number>();
    const corrDeltaByParent = new Map<number, number>();
    const corrIdToParent = new Map<number, number>();
    if (invoiceIds.length === 0) return { grossById, corrDeltaByParent, corrIdToParent };

    const { data: invRows, error: invErr } = await this.sb
      .from('invoices')
      .select('id, gross_amount')
      .in('id', invoiceIds);
    if (invErr) throw invErr;
    type Slim = { id: number; gross_amount: number | null };
    for (const r of (invRows ?? []) as Slim[]) {
      grossById.set(r.id, Number(r.gross_amount ?? 0));
    }

    const { data: corrRows, error: corrErr } = await this.sb
      .from('invoices')
      .select('id, correction_of_invoice_id, gross_amount')
      .in('correction_of_invoice_id', invoiceIds)
      .eq('invoice_type', 'correction')
      .eq('is_deleted', false);
    if (corrErr) throw corrErr;
    type CorrSlim = { id: number; correction_of_invoice_id: number; gross_amount: number | null };
    for (const c of (corrRows ?? []) as CorrSlim[]) {
      const parent = c.correction_of_invoice_id;
      corrDeltaByParent.set(parent, (corrDeltaByParent.get(parent) ?? 0) + Number(c.gross_amount ?? 0));
      corrIdToParent.set(c.id, parent);
    }

    return { grossById, corrDeltaByParent, corrIdToParent };
  }

  /**
   * Look up just the (id, invoice_number) pair for a set of invoices.
   * Used by the line-item reconcile picker to label "Powiązana z FV/<n>"
   * for candidate rows attached to other invoices, without round-tripping
   * a full Refine `useList` from inside the form.
   */
  async getNumbersByIds(ids: number[]): Promise<Map<number, string>> {
    if (ids.length === 0) return new Map();
    const { data, error } = await this.sb
      .from('invoices')
      .select('id, invoice_number')
      .in('id', ids);
    if (error) throw error;
    const out = new Map<number, string>();
    for (const row of (data ?? []) as { id: number; invoice_number: string }[]) {
      out.set(row.id, row.invoice_number);
    }
    return out;
  }

  // --- Line item queries ---

  async getLineItems(invoiceId: number): Promise<InvoiceLineRow[]> {
    const { data, error } = await this.sb
      .from('invoice_line_items')
      .select('*')
      .eq('invoice_id', invoiceId)
      .order('ordinal', { ascending: true });
    if (error) throw error;
    return ((data as InvoiceLineRow[]) ?? []).map(enrichLineItem);
  }

  /** Fetch line items for multiple invoices, grouped by invoice_id. */
  async getLineItemsForInvoices(invoiceIds: number[]): Promise<Map<number, InvoiceLineRow[]>> {
    if (invoiceIds.length === 0) return new Map();
    const { data, error } = await this.sb
      .from('invoice_line_items')
      .select('*')
      .in('invoice_id', invoiceIds)
      .order('ordinal', { ascending: true });
    if (error) throw error;
    const map = new Map<number, InvoiceLineRow[]>();
    for (const row of (data as InvoiceLineRow[]) ?? []) {
      enrichLineItem(row);
      const bucket = map.get(row.invoice_id);
      if (bucket) bucket.push(row);
      else map.set(row.invoice_id, [row]);
    }
    return map;
  }

  // --- Link queries ---

  /** Fetch links as InvoiceLink app type — with computed pricing. */
  async getResolvedLinks(invoiceId: number): Promise<InvoiceLink[]> {
    const { data, error } = await this.sb
      .from('invoice_resolved_links')
      .select('*')
      .eq('invoice_id', invoiceId);
    if (error) throw error;
    return ((data ?? []) as InvoiceEntityView[]).map(enrichInvoiceLink);
  }


  /** Fetch raw link rows for an invoice. */
  async getLinkRows(invoiceId: number): Promise<InvoiceLinkRow[]> {
    const { data, error } = await this.sb
      .from('invoice_links')
      .select('*')
      .eq('invoice_id', invoiceId);
    if (error) throw error;
    return (data as InvoiceLinkRow[]) ?? [];
  }

  // --- Reconcile candidate view ---

  /** Fetch FREE reconcile candidates for a single vendor (picker "Wolne" mode), optionally
   *  filtered by side. candidate_sides carries all sides; free = `linked_invoice_id` null. */
  async getCandidatesForVendor(
    vendorId: number,
    opts?: { side?: TransactionSideType },
  ): Promise<InvoiceLink[]> {
    let q = this.sb
      .from('candidate_sides')
      .select('*')
      .eq('vendor_id', vendorId)
      .is('linked_invoice_id', null)
      .order('date', { ascending: false });
    if (opts?.side === 'transport') {
      q = q.eq('entity_type', 'transport');
    } else if (opts?.side) {
      q = q.eq('entity_type', 'transaction').eq('side', opts.side);
    }
    const { data, error } = await q;
    if (error) throw error;
    return ((data ?? []) as InvoiceEntityView[]).map(enrichInvoiceLink);
  }

  /** Fetch reconcile candidates for multiple vendors (deduped).
   *
   *  PAGINATED: a single batch spans many vendors and the candidate pool can
   *  exceed PostgREST's 1000-row cap. A hard `.limit(1000)` silently truncated
   *  the pool — candidates in the dropped tail produced spurious
   *  `p1_ref_unmatched` even when a free, exact-reference transaction existed
   *  (it was just past row 1000). Page through `.range()` until a short page so
   *  the whole pool is fetched regardless of size. */
  async getCandidatesForVendors(vendorIds: number[]): Promise<InvoiceLink[]> {
    if (vendorIds.length === 0) return [];
    const pageSize = 1000;
    const rows: InvoiceEntityView[] = [];
    for (let from = 0; ; from += pageSize) {
      const { data, error } = await this.sb
        .from('candidate_sides')
        // REC-m9: candidate_sides is a UNION ALL (transaction load/unload + transport
        // legs), so entity_id alone is NOT unique across the view — paging on it gave an
        // unstable sort that could drop/duplicate rows at page boundaries. Order by the
        // full row identity (entity_type, entity_id, side) so .range() paging is stable.
        .select('*')
        .in('vendor_id', vendorIds)
        .order('entity_type', { ascending: true })
        .order('entity_id', { ascending: true })
        .order('side', { ascending: true, nullsFirst: true })
        .range(from, from + pageSize - 1);
      if (error) throw error;
      const page = (data ?? []) as InvoiceEntityView[];
      rows.push(...page);
      if (page.length < pageSize) break;
    }
    const all = rows.map(enrichInvoiceLink);
    const seen = new Set<string>();
    return all.filter(c => {
      const key = `${c.entity_type}:${c.entity_id}:${c.side ?? ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /** MAJOR-5: sides that `candidate_sides` EXCLUDES because their price is NULL or 0.
   *
   *  `candidate_sides` filters out unpriced sides (schema WHERE `load_price/unload_price/
   *  price IS NOT NULL AND <> 0`), so a reference that names a real-but-unpriced transaction
   *  never appears in the vendor pool — the matcher then reports a generic `p1_ref_unmatched`
   *  and the `ref_found_tx_unpriced` diagnosis is unreachable. This reads the UNDERLYING
   *  tables directly (transactions load/unload sides + transaction_transports) for the
   *  vendor family, keeping ONLY the unpriced sides, and shapes them as `InvoiceLink`
   *  candidates for ref-miss DIAGNOSIS ONLY (`diagnoseRefMiss`). These rows must never enter
   *  the working match pool — they carry a null/0 price by construction.
   *
   *  Mirrors candidate_sides' vendor/side semantics minimally: transaction load side keyed
   *  on `load_commercial_company_id`, unload on `unload_commercial_company_id`; transport on
   *  `commercial_company_id` (the payer — defaults to the carrier via trigger), reference
   *  taken from the parent transaction. `linked_invoice_id` is left null (an unpriced side
   *  is excluded from the pool and not linked). */
  async getUnpricedRefSidesForVendors(vendorIds: number[]): Promise<InvoiceLink[]> {
    if (vendorIds.length === 0) return [];

    const [loadRes, unloadRes, transportRes] = await Promise.all([
      // Transaction LOAD sides with no load price, vendor on the load side.
      this.sb
        .from('transactions')
        .select('id, reference_number, unloaded_weight, load_price, load_price_type, load_price_currency, load_price_vat_rate_code, load_commercial_company_id')
        .eq('is_deleted', false)
        .in('load_commercial_company_id', vendorIds)
        .or('load_price.is.null,load_price.eq.0'),
      // Transaction UNLOAD sides with no unload price, vendor on the unload side.
      this.sb
        .from('transactions')
        .select('id, reference_number, unloaded_weight, unload_price, unload_price_type, unload_price_currency, unload_price_vat_rate_code, unload_commercial_company_id')
        .eq('is_deleted', false)
        .in('unload_commercial_company_id', vendorIds)
        .or('unload_price.is.null,unload_price.eq.0'),
      // Transport legs with no price, vendor = commercial_company_id (payer); ref from the parent tx.
      this.sb
        .from('transaction_transports')
        .select('id, price, price_type, price_currency, price_vat_rate_code, transport_company_id, commercial_company_id, transaction:transactions!inner(id, reference_number, unloaded_weight)')
        .eq('is_deleted', false)
        .in('commercial_company_id', vendorIds)
        .or('price.is.null,price.eq.0'),
    ]);
    if (loadRes.error) throw loadRes.error;
    if (unloadRes.error) throw unloadRes.error;
    if (transportRes.error) throw transportRes.error;

    const rows: InvoiceEntityView[] = [];

    for (const t of (loadRes.data ?? []) as Record<string, unknown>[]) {
      rows.push(this.unpricedTransactionSide(t, 'load'));
    }
    for (const t of (unloadRes.data ?? []) as Record<string, unknown>[]) {
      rows.push(this.unpricedTransactionSide(t, 'unload'));
    }
    for (const tp of (transportRes.data ?? []) as Record<string, unknown>[]) {
      const tx = (tp.transaction ?? {}) as Record<string, unknown>;
      rows.push({
        entity_type: 'transport',
        entity_id: tp.id as number,
        transaction_id: (tx.id as number) ?? 0,
        side: null,
        unit_price: (tp.price as number | null) ?? null,
        currency: (tp.price_currency as string | null) ?? null,
        price_type: (tp.price_type as PriceType | null) ?? null,
        vat_rate_code: (tp.price_vat_rate_code as string | null) ?? null,
        date: null,
        weight: (tx.unloaded_weight as number | null) ?? null,
        reference_number: (tx.reference_number as string | null) ?? null,
        product_id: null,
        product_name: null,
        product_waste_code: null,
        transport_company_id: (tp.transport_company_id as number | null) ?? null,
        transport_company_name: null,
        vendor_id: (tp.commercial_company_id as number | null) ?? null,
        linked_invoice_id: null,
      } as InvoiceEntityView);
    }

    return rows.map(enrichInvoiceLink);
  }

  /** Shape one unpriced transaction side (load|unload) into an `InvoiceEntityView`. */
  private unpricedTransactionSide(t: Record<string, unknown>, side: 'load' | 'unload'): InvoiceEntityView {
    const priced = side === 'load'
      ? { price: t.load_price, type: t.load_price_type, currency: t.load_price_currency, vat: t.load_price_vat_rate_code, vendor: t.load_commercial_company_id }
      : { price: t.unload_price, type: t.unload_price_type, currency: t.unload_price_currency, vat: t.unload_price_vat_rate_code, vendor: t.unload_commercial_company_id };
    return {
      entity_type: 'transaction',
      entity_id: t.id as number,
      transaction_id: t.id as number,
      side,
      unit_price: (priced.price as number | null) ?? null,
      currency: (priced.currency as string | null) ?? null,
      price_type: (priced.type as PriceType | null) ?? null,
      vat_rate_code: (priced.vat as string | null) ?? null,
      date: null,
      weight: (t.unloaded_weight as number | null) ?? null,
      reference_number: (t.reference_number as string | null) ?? null,
      product_id: null,
      product_name: null,
      product_waste_code: null,
      transport_company_id: null,
      transport_company_name: null,
      vendor_id: (priced.vendor as number | null) ?? null,
      linked_invoice_id: null,
    } as InvoiceEntityView;
  }

  // --- Line item writes ---

  /** Bulk insert line items, returns created rows with IDs. */
  async createLineItems(items: InvoiceLineInsert[], opts?: import('../base.ts').MutationOptions): Promise<InvoiceLineRow[]> {
    if (items.length === 0) return [];
    const { data, error } = await this.sb
      .from('invoice_line_items')
      .insert(items as Record<string, unknown>[])
      .select('*');
    if (error) throw error;
    this.handleInvalidation(opts);
    return (data as InvoiceLineRow[]) ?? [];
  }

  /** Batch update line items by ID. */
  async updateLineItems(rows: Array<Partial<InvoiceLineRow> & { id: number }>, opts?: import('../base.ts').MutationOptions): Promise<void> {
    if (rows.length === 0) return;
    await Promise.all(rows.map(({ id, ...fields }) =>
      this.sb.from('invoice_line_items').update(fields as Record<string, unknown>).eq('id', id).then(({ error }) => { if (error) throw error; }),
    ));
    this.handleInvalidation(opts);
  }

  /** Delete all line items for an invoice. */
  async deleteLineItems(invoiceId: number, opts?: import('../base.ts').MutationOptions): Promise<void> {
    const { error } = await this.sb
      .from('invoice_line_items')
      .delete()
      .eq('invoice_id', invoiceId);
    if (error) throw error;
    this.handleInvalidation(opts);
  }

  /** Delete specific line items by IDs. */
  async deleteLineItemsByIds(ids: number[], opts?: import('../base.ts').MutationOptions): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await this.sb
      .from('invoice_line_items')
      .delete()
      .in('id', ids);
    if (error) throw error;
    this.handleInvalidation(opts);
  }

  // --- Link writes ---

  /** Bulk insert links (deduped by invoice_id:entity_id:side). */
  async createLinks(links: InvoiceLinkInsert[], opts?: import('../base.ts').MutationOptions): Promise<InvoiceLinkRow[]> {
    if (links.length === 0) return [];
    const seen = new Set<string>();
    const deduped = links.filter(l => {
      const entityId = l.transaction_id ?? l.transaction_transport_id;
      const key = `${l.invoice_id}:${entityId}:${l.side ?? ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const { data, error } = await this.sb
      .from('invoice_links')
      .insert(deduped as Record<string, unknown>[])
      .select('*');
    if (error) throw error;
    this.handleInvalidation(opts);
    return (data as InvoiceLinkRow[]) ?? [];
  }

  /** Apply a whole reconcile batch atomically via the `reconcile_apply` SQL function:
   *  delete the affected invoices' links, insert the new ones, set status + report, and
   *  propagate approved status to corrections — all in ONE transaction / round trip.
   *  Replaces the delete + insert + per-row updateMany + status-bulk client calls, which
   *  fired ~N concurrent requests and timed out / crashed the container on a full sweep. */
  async applyReconcileWrite(payload: ReconcileWritePayload, opts?: import('../base.ts').MutationOptions): Promise<ReconcileApplySummary | void> {
    // `sb` is an untyped SupabaseClient, so rpc().data is `any` — assert the SQL return type.
    const res = await this.sb.rpc('reconcile_apply', { p: payload as unknown as Record<string, unknown> });
    if (res.error) throw res.error;
    this.handleInvalidation(opts);
    // reconcile_apply now returns a guard-skip summary (finding 4). A null RPC result
    // (older function version) → "nothing skipped". Return type stays `| undefined` so
    // pre-existing callers that mock a void resolve still type-check; writeResults guards.
    const summary = res.data as ReconcileApplySummary | null;
    return summary ?? undefined;
  }

  /** Delete all links for an invoice. */
  async deleteLinks(invoiceId: number, opts?: import('../base.ts').MutationOptions): Promise<void> {
    const { error } = await this.sb
      .from('invoice_links')
      .delete()
      .eq('invoice_id', invoiceId);
    if (error) throw error;
    this.handleInvalidation(opts);
  }

  /** Delete all links for multiple invoices. */
  async deleteLinksForInvoices(invoiceIds: number[], opts?: import('../base.ts').MutationOptions): Promise<void> {
    if (invoiceIds.length === 0) return;
    const { error } = await this.sb
      .from('invoice_links')
      .delete()
      .in('invoice_id', invoiceIds);
    if (error) throw error;
    this.handleInvalidation(opts);
  }

  /** Delete specific links by ID. */
  async deleteLinksByIds(ids: number[], opts?: import('../base.ts').MutationOptions): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await this.sb
      .from('invoice_links')
      .delete()
      .in('id', ids);
    if (error) throw error;
    this.handleInvalidation(opts);
  }

  /** Clear stale flag on all links for an invoice. */
  async clearStaleLinks(invoiceId: number, opts?: import('../base.ts').MutationOptions): Promise<void> {
    const { error } = await this.sb
      .from('invoice_links')
      .update({ stale: false })
      .eq('invoice_id', invoiceId)
      .eq('stale', true);
    if (error) throw error;
    this.handleInvalidation(opts);
  }

  // --- Invoice status updates ---

  /** Update status fields on a single invoice. */
  async updateStatus(invoiceId: number, fields: Partial<Pick<InvoiceRow, 'review_status' | 'reconcile_report' | 'forced' | 'override_mode' | 'payment_status' | 'hold_reason' | 'payment_method' | 'sync_status' | 'pending_changes' | 'fakturownia_dirty'>>, opts?: import('../base.ts').MutationOptions): Promise<void> {
    const { error } = await this.sb
      .from('invoices')
      .update(fields as Record<string, unknown>)
      .eq('id', invoiceId);
    if (error) throw error;
    this.handleInvalidation(opts);
  }

  /**
   * Update payment status with PENDING keyword logic and dirty flag for deferred Fakturownia sync.
   * Handles: paid_date auto-set, hold_reason clear, PENDING prefix in notes.
   * Sets fakturownia_dirty=true so the batch job picks it up.
   */
  async updatePaymentStatusWithNotes(
    invoiceId: number,
    paymentStatus: string,
    holdReason?: string | null,
    opts?: import('../base.ts').MutationOptions,
  ): Promise<void> {
    // Fetch current notes for PENDING keyword logic
    const { data: current, error: fetchErr } = await this.sb
      .from('invoices')
      .select('notes')
      .eq('id', invoiceId)
      .single();
    if (fetchErr) throw fetchErr;

    const currentNotes: string | null = (current as { notes: string | null } | null)?.notes ?? null;
    const update: Record<string, unknown> = {
      payment_status: paymentStatus,
      fakturownia_dirty: true,
    };

    // Auto-set paid_date
    if (paymentStatus === 'paid') {
      update.paid_date = new Date().toISOString().split('T')[0];
    }

    // Hold reason
    if (paymentStatus === 'on_hold' && holdReason !== undefined) {
      update.hold_reason = holdReason;
    }
    if (paymentStatus !== 'on_hold') {
      update.hold_reason = null;
    }

    // PENDING keyword logic — same definition the bulk path uses, so a hold set
    // from the UI and one set by the sweep produce identical notes.
    update.notes = paymentStatus === 'on_hold'
      ? withPendingMarker(currentNotes)
      : withoutPendingMarker(currentNotes);

    const { error: updateErr } = await this.sb
      .from('invoices')
      .update(update)
      .eq('id', invoiceId);
    if (updateErr) throw updateErr;
    this.handleInvalidation(opts);
  }

  /**
   * Update invoice fields locally with dirty flag for deferred Fakturownia sync.
   * For payment_status changes, delegates to updatePaymentStatusWithNotes().
   * For other fields (review_status, payment_method), updates directly.
   */
  async updateInvoiceFields(
    invoiceId: number,
    fields: Partial<Pick<InvoiceRow, 'review_status' | 'payment_status' | 'payment_method'>>,
    holdReason?: string | null,
    opts?: import('../base.ts').MutationOptions,
  ): Promise<void> {
    // If payment_status is being changed, use the full PENDING logic
    if (fields.payment_status) {
      await this.updatePaymentStatusWithNotes(invoiceId, fields.payment_status, holdReason, { invalidate: false });
      // Update remaining fields (if any) separately
      const rest = { ...fields };
      delete rest.payment_status;
      if (Object.keys(rest).length > 0) {
        await this.updateStatus(invoiceId, rest, { invalidate: false });
      }
    } else {
      // No payment_status change — simple update, still mark dirty for sync
      await this.updateStatus(invoiceId, { ...fields, fakturownia_dirty: true } as Parameters<typeof this.updateStatus>[1], { invalidate: false });
    }
    this.handleInvalidation(opts);
  }

  /** Bulk update status fields on multiple invoices.
   *
   * A18 write-guards: the only caller is the payment-hold sweep
   * (InvoicePaymentService.enforceInvoiceHolds), whose decisions are computed on
   * a snapshot read. The recompute_invoice_payment_status trigger can flip a row
   * to 'paid' between that read and this write — a stale sweep decision must
   * never demote it. Guards:
   *   - any non-paid payment_status write skips rows that are now 'paid';
   *   - a release write (→ pending/partially_paid with hold_reason cleared) only fires
   *     on rows still 'on_hold' (the status the release decision was based on).
   */
  async updateStatusBulk(ids: number[], fields: Partial<Pick<InvoiceRow, 'review_status' | 'reconcile_report' | 'forced' | 'override_mode' | 'payment_status' | 'hold_reason' | 'payment_method' | 'sync_status' | 'pending_changes'>>, opts?: import('../base.ts').MutationOptions): Promise<void> {
    if (ids.length === 0) return;
    let q = this.sb
      .from('invoices')
      .update(fields as Record<string, unknown>)
      .in('id', ids);
    const newStatus = fields.payment_status as string | undefined;
    if (newStatus !== undefined && newStatus !== 'paid') {
      const isReleaseWrite =
        (newStatus === 'pending' || newStatus === 'partially_paid') &&
        'hold_reason' in fields && fields.hold_reason === null;
      q = isReleaseWrite
        ? q.eq('payment_status', 'on_hold')
        : q.neq('payment_status', 'paid');
    }
    const { error } = await q;
    if (error) throw error;
    this.handleInvalidation(opts);
  }

  // --- Hard delete ---

  /** Permanently delete an invoice with all links, line items, audit log, and bank accounts.
   *  Links must be deleted first (no CASCADE), everything else cascades. */
  async permanentDelete(invoiceId: number, opts?: import('../base.ts').MutationOptions): Promise<void> {
    await this.deleteLinks(invoiceId, { invalidate: false });
    await this.delete(invoiceId, { invalidate: false });
    this.handleInvalidation(opts);
  }

  // --- Bank account queries ---

  async getBankAccounts(invoiceId: number): Promise<InvoiceBankAccountRow[]> {
    const { data, error } = await this.sb
      .from('invoice_bank_accounts')
      .select('*')
      .eq('invoice_id', invoiceId);
    if (error) throw error;
    return (data as InvoiceBankAccountRow[]) ?? [];
  }

  /** Minimal invoice projection for the bank-account correction gate
   *  (InvoiceAccountAssignService, R2): direction + current seller_bank_account (must
   *  be empty to assign) + the counterparty company id (a received invoice's vendor,
   *  to check the picked registry row belongs to it) + external_system_id — the
   *  Fakturownia id, same field KsefVerificationService/ShadowDao findings key on —
   *  so a synced invoice's correction can open an audit-trail KSeF finding. Null when
   *  the invoice doesn't exist. */
  async getForBankAccountAssign(invoiceId: number): Promise<InvoiceBankAccountAssignRow | null> {
    const { data, error } = await this.sb
      .from('invoices')
      .select('id, direction, seller_bank_account, seller_company_id, external_system_id, payment_status, fakturownia_payment_status')
      .eq('id', invoiceId)
      .maybeSingle();
    if (error) throw error;
    return data as InvoiceBankAccountAssignRow | null;
  }

  /** Write the operator-assigned canonical seller_bank_account (R2 bank-account
   *  correction). Caller (InvoiceAccountAssignService) is responsible for the
   *  eligibility checks and for canonicalizing the account number first. */
  async setSellerBankAccount(invoiceId: number, accountNumber: string, opts?: import('../base.ts').MutationOptions): Promise<void> {
    // .select() verifies the row was actually written: RLS filters silently
    // (an unauthorized role gets UPDATE 0 with NO error), and the caller's UI
    // must not report success for a write that never landed.
    const { data, error } = await this.sb
      .from('invoices')
      .update({ seller_bank_account: accountNumber })
      .eq('id', invoiceId)
      .select('id');
    if (error) throw error;
    if ((data ?? []).length === 0) {
      throw new Error('Brak uprawnień do zmiany rachunku na fakturze (wymagana rola admin/management/administration)');
    }
    this.handleInvalidation(opts);
  }

  // --- Payment hold queries ---

  /**
   * Fetch all received, non-deleted invoices eligible for the hold policy
   * (payment_status in pending/on_hold/partially_paid), with LEFT-joined links → transports →
   * transactions → rules. LEFT joins (not !inner) so NON-transport and
   * UNapproved invoices are returned too — the policy evaluator decides per row.
   */
  async getReceivedInvoicesForHoldCheck(invoiceId?: number): Promise<ReceivedHoldCheckRow[]> {
    let q = this.sb
      .from('invoices')
      .select(`
        id, payment_status, hold_reason, review_status, payment_method, paid_amount,
        seller_company_id, seller_bank_account,
        invoice_links(
          stale,
          transaction_transport_id,
          transaction_transports(
            id, transaction_id,
            transactions(
              id, rule_id,
              transaction_rules(annex_original_approved_required, cmr_original_approved_required)
            )
          )
        )
      `)
      .eq('direction', 'received')
      .eq('is_deleted', false)
      // partially_paid is included so an unapproved/missing-doc invoice that already
      // received a bank payment is still held; releasing the hold lets the ladder
      // land it back on partially_paid.
      .in('payment_status', ['pending', 'on_hold', 'partially_paid']);
    if (invoiceId != null) q = q.eq('id', invoiceId);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as unknown as ReceivedHoldCheckRow[];
  }

  /** Fetch approved original documents for given transaction IDs. */
  async getApprovedOriginalDocs(transactionIds: number[]): Promise<{ transaction_id: number; document_type: string }[]> {
    if (transactionIds.length === 0) return [];
    const { data, error } = await this.sb
      .from('transaction_files')
      .select('transaction_id, document_type')
      .in('transaction_id', transactionIds)
      .eq('is_latest', true)
      .in('document_type', ['annex7', 'cmr'])
      .eq('document_status', 'original')
      .eq('review_status', 'approved');
    if (error) throw error;
    return (data ?? []) as { transaction_id: number; document_type: string }[];
  }

  /** Payable account cores per vendor (payable set = payment_verified/operator_verified),
   *  keyed by company, values are ibanCore-normalized account numbers. */
  async getVerifiedAccountCores(companyIds: number[]): Promise<Map<number, Set<string>>> {
    if (companyIds.length === 0) return new Map();
    const { data, error } = await this.sb
      .from('company_bank_accounts')
      .select('company_id, account_number')
      .in('company_id', companyIds)
      .in('verification_status', ['payment_verified', 'operator_verified']);
    if (error) throw error;
    const m = new Map<number, Set<string>>();
    for (const r of (data ?? []) as { company_id: number; account_number: string }[]) {
      const set = m.get(r.company_id) ?? new Set<string>();
      set.add(ibanCore(r.account_number));
      m.set(r.company_id, set);
    }
    return m;
  }

  /** Own-company bank accounts keyed by currency (currency → account_number),
   *  for outbound department/bank-account routing (resolveFakturowniaRouting).
   *  Mirrors resolveOwnDebtor's own-company lookup: find the singleton
   *  `is_own_company=true` company, then its `company_bank_accounts` rows.
   *  Excludes `is_vat_account=true` rows (a VAT sub-account is never the
   *  account we quote on an outbound invoice). When a currency has several
   *  eligible rows, `is_default` wins, then the lowest id. */
  async listOwnCompanyBankAccounts(): Promise<Record<string, string>> {
    const { data: company, error: coErr } = await this.sb
      .from('companies')
      .select('id')
      .eq('is_own_company', true)
      .maybeSingle();
    if (coErr) throw coErr;
    const companyId = (company as { id: number } | null)?.id;
    if (companyId == null) return {};

    const { data, error } = await this.sb
      .from('company_bank_accounts')
      .select('currency, account_number, is_default, id')
      .eq('company_id', companyId)
      .eq('is_vat_account', false)
      .order('is_default', { ascending: false })
      .order('id', { ascending: true });
    if (error) throw error;

    const out: Record<string, string> = {};
    for (const row of (data ?? []) as { currency: string | null; account_number: string; is_default: boolean | null; id: number }[]) {
      if (!row.currency || row.currency in out) continue;
      out[row.currency] = row.account_number;
    }
    return out;
  }

  /** Full names of active employees, for reimbursement-invoice detection
   *  (resolveFakturowniaRouting matches vendor names against these). */
  async listEmployeeFullNames(): Promise<string[]> {
    const { data, error } = await this.sb
      .from('employees')
      .select('full_name')
      .eq('is_deleted', false);
    if (error) throw error;
    return ((data ?? []) as { full_name: string }[]).map(r => r.full_name);
  }

  /** `fakturownia_departments` app-configuration value (currency, or the
   *  special `WYNAGRODZENIE` key, → Fakturownia department id) for outbound
   *  routing (resolveFakturowniaRouting). Stored as JSON — ids may be numeric
   *  — normalized to strings for the routing map. Missing/malformed config
   *  returns {} rather than throwing, so a caller that degrades on lookup
   *  failure also degrades on "row simply isn't there yet". */
  async getFakturowniaDepartmentsConfig(): Promise<Record<string, string>> {
    const { data, error } = await this.sb
      .from('app_configuration')
      .select('value')
      .eq('key', 'fakturownia_departments')
      .maybeSingle();
    if (error) throw error;
    const raw = (data as { value: unknown } | null)?.value;
    if (!raw || typeof raw !== 'object') return {};
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof value === 'string') out[key] = value;
      else if (typeof value === 'number') out[key] = String(value);
    }
    return out;
  }

  /** Company short/full names for a set of ids, in ONE batched query — the
   *  outbound routing pass resolves every candidate's vendor name up front
   *  instead of querying per invoice. Chunked like getByExternalIds. */
  async listCompanyNamesByIds(ids: number[]): Promise<Map<number, string>> {
    const out = new Map<number, string>();
    if (ids.length === 0) return out;
    const CHUNK_SIZE = 200;
    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
      const { data, error } = await this.sb
        .from('companies')
        .select('id, short_name, full_name')
        .in('id', ids.slice(i, i + CHUNK_SIZE));
      if (error) throw error;
      for (const row of (data ?? []) as { id: number; short_name: string | null; full_name: string | null }[]) {
        out.set(row.id, row.short_name || row.full_name || '');
      }
    }
    return out;
  }

  // --- Approval queries ---

  /** Fetch pending original invoices for auto-approval. */
  async getPendingForApproval(invoiceId?: number): Promise<InvoiceRow[]> {
    let q = this.sb
      .from('invoices')
      .select('*')
      .eq('is_deleted', false)
      .eq('review_status', 'pending')
      .eq('invoice_type', 'original');
    if (invoiceId != null) q = q.eq('id', invoiceId);
    const { data, error } = await q.order('id');
    if (error) throw error;
    return (data ?? []) as InvoiceRow[];
  }

  /** Fetch active approval rules ordered by priority. */
  async getApprovalRules(): Promise<unknown[]> {
    const { error, data } = await this.sb
      .from('invoice_approval_rules')
      .select('*')
      .eq('is_active', true)
      .order('priority');
    if (error) throw error;
    return (data as unknown[] | null) ?? [];
  }

  /** Fetch active warning clauses (suspicious-phrase dictionary). */
  async getWarningClauses(): Promise<WarningClause[]> {
    const { error, data } = await this.sb
      .from('invoice_warning_clauses')
      .select('*')
      .eq('is_active', true)
      .order('id');
    if (error) throw error;
    return (data as WarningClause[] | null) ?? [];
  }

  /** Already-approved invoice aggregates per (vendor, sell-month) for approval
   *  threshold checks, keyed `"<vendorId>:<YYYY-MM>"`.
   *
   *  Buckets by the invoice's OWN sell_date month — the monthly caps
   *  (max_total_net_per_month / max_invoices_per_month) are about the calendar
   *  month the invoice was issued in, NOT when it happens to be reconciled. A
   *  multi-month batch must not be lumped into one month.
   *
   *  Baseline counts only `approved` (committed) invoices — NOT `pending`. The
   *  pending invoices are the ones being evaluated; including them double-counted
   *  the backlog against the cap (so a full re-reconcile failed every rule). */
  async getMonthlyAggregates(vendorIds: number[], startDate: string, endDate: string): Promise<Map<string, { count: number; totalNet: number }>> {
    const result = new Map<string, { count: number; totalNet: number }>();
    if (vendorIds.length === 0) return result;
    const { data, error } = await this.sb
      .from('invoices')
      .select('seller_company_id, buyer_company_id, direction, net_amount, sell_date')
      .eq('is_deleted', false)
      .eq('review_status', 'approved')
      .gte('sell_date', startDate)
      .lt('sell_date', endDate);
    if (error) throw error;
    type AggRow = Pick<InvoiceRow, 'seller_company_id' | 'buyer_company_id' | 'direction' | 'net_amount' | 'sell_date'>;
    for (const row of (data as AggRow[] | null) ?? []) {
      const vid = row.direction === 'received' ? row.seller_company_id : row.buyer_company_id;
      // A null vendor id was never a member of the number[] pool, so skipping is equivalent.
      if (vid == null || !vendorIds.includes(vid)) continue;
      const sellDate = row.sell_date;
      if (!sellDate) continue;
      const month = monthOf(sellDate);
      const key = `${vid}:${month}`;
      const agg = result.get(key) ?? { count: 0, totalNet: 0 };
      agg.count++;
      agg.totalNet += Math.abs(row.net_amount ?? 0);
      result.set(key, agg);
    }
    return result;
  }
  // --- Vendor transactions (all, including invoiced) ---

  /** Fetch ALL transaction sides for vendor IDs — free AND already-linked (picker
   *  "Wszystkie" mode). Reads candidate_sides; `linked_invoice_id` (null = free) tells the
   *  picker which sides are taken (and by whom, resolved via getInvoiceNumbersByIds). The
   *  `search` arg is accepted for compatibility but filtering is client-side in the form
   *  (candidate_sides has no search_vector). */
  async getAllVendorTransactions(vendorIds: number[], _search?: string): Promise<InvoiceLink[]> {
    if (vendorIds.length === 0) return [];
    const { data, error } = await this.sb
      .from('candidate_sides')
      .select('*')
      .in('vendor_id', vendorIds)
      .order('date', { ascending: false })
      .limit(1000);
    if (error) throw error;
    return ((data ?? []) as InvoiceEntityView[]).map(enrichInvoiceLink);
  }

  /** Fetch ONE page of transaction sides across EVERY vendor (picker "Globalnie" mode) —
   *  free AND already linked. NO vendor filter and NO `linked_invoice_id` filter: a
   *  locate-anywhere browser so the operator finds where a transaction is, even cross-vendor
   *  or attached to another invoice. Server-side paginated + searched (no client-side
   *  load-all), so the set stays small (one page) regardless of total transaction volume.
   *
   *  `query` (trimmed length ≥ 2) filters by `reference_number` (ilike). Returns the page
   *  rows plus the exact total match count for the pager. */
  async searchCandidatesPage(
    opts: { query?: string; page: number; pageSize: number },
  ): Promise<{ rows: InvoiceLink[]; total: number }> {
    const { page, pageSize } = opts;
    const q = (opts.query ?? '').trim();
    let query = this.sb
      .from('candidate_sides')
      .select('*', { count: 'exact' });
    if (q.length >= 2) {
      query = query.ilike('reference_number', `%${q}%`);
    }
    const { data, error, count } = await query
      .order('date', { ascending: false })
      .range(page * pageSize, page * pageSize + pageSize - 1);
    if (error) throw error;
    return {
      rows: ((data ?? []) as InvoiceEntityView[]).map(enrichInvoiceLink),
      total: count ?? 0,
    };
  }

  // --- Conflict resolution ---

  /** Accept a single field from pending_changes. */
  async acceptField(invoiceId: number, field: string, opts?: import('../base.ts').MutationOptions): Promise<void> {
    const VALID_FIELDS = new Set([
      'invoice_number', 'invoice_date', 'issue_date', 'sell_date', 'payment_date', 'paid_date',
      'payment_method', 'net_amount', 'vat_amount', 'gross_amount', 'currency',
      'fakturownia_status', 'seller_bank_name', 'seller_bank_account', 'split_payment', 'issue_place',
    ]);
    if (!VALID_FIELDS.has(field)) throw new Error(`Invalid field: ${field}`);

    const inv = await this.getById(invoiceId);
    const pending: Array<{ field: string; local_value: unknown; remote_value: unknown }> = inv.pending_changes ?? [];
    const change = pending.find(c => c.field === field);
    if (!change) return;

    const remaining = pending.filter(c => c.field !== field);
    await this.sb.from('invoices').update({ [field]: change.remote_value }).eq('id', invoiceId);
    const { error } = await this.sb.from('invoices').update({
      pending_changes: remaining.length > 0 ? remaining : null,
      sync_status: remaining.length === 0 ? 'synced' : 'conflict',
    }).eq('id', invoiceId);
    if (error) throw error;
    this.handleInvalidation(opts);
  }

  /** Dismiss all pending changes. */
  async dismissChanges(invoiceId: number, opts?: import('../base.ts').MutationOptions): Promise<void> {
    const inv = await this.getById(invoiceId);
    const { error } = await this.sb.from('invoices').update({
      pending_changes: null,
      sync_status: inv.external_system_id ? 'synced' : 'unlinked',
    }).eq('id', invoiceId);
    if (error) throw error;
    this.handleInvalidation(opts);
  }

  // --- Sync support ---

  /**
   * Fetch invoices by external_system_id (Fakturownia IDs).
   *
   * Chunked to avoid PostgREST URL-length limits — `external_system_id=in.(...)`
   * grows linearly with the list, and a full from-EPOCH sync may pass
   * thousands of ids. Empirically the proxy starts rejecting around 8–16KB
   * URL; 200 ids/chunk stays well below at any realistic id length.
   */
  async getByExternalIds(externalIds: string[]): Promise<InvoiceRow[]> {
    if (externalIds.length === 0) return [];
    const CHUNK_SIZE = 200;
    const chunks: string[][] = [];
    for (let i = 0; i < externalIds.length; i += CHUNK_SIZE) {
      chunks.push(externalIds.slice(i, i + CHUNK_SIZE));
    }
    const results = await Promise.all(chunks.map(async (chunk) => {
      const { data, error } = await this.sb
        .from('invoices')
        .select('*')
        .in('external_system_id', chunk);
      if (error) throw error;
      return (data ?? []) as InvoiceRow[];
    }));
    return results.flat();
  }

  /** List all Fakturownia-sourced invoices for a direction (paginated to bypass 1000-row default). */
  async listFakturowniaInvoicesByDirection(
    direction: InvoiceDirection,
  ): Promise<Pick<InvoiceRow, 'id' | 'external_system_id' | 'invoice_number' | 'gross_amount' | 'invoice_date'>[]> {
    const out: Pick<InvoiceRow, 'id' | 'external_system_id' | 'invoice_number' | 'gross_amount' | 'invoice_date'>[] = [];
    const pageSize = 1000;
    let from = 0;
    while (true) {
      const { data, error } = await this.sb
        .from('invoices')
        .select('id, external_system_id, invoice_number, gross_amount, invoice_date')
        .eq('source', 'fakturownia')
        .eq('direction', direction)
        .eq('is_deleted', false)
        .not('external_system_id', 'is', null)
        .order('id', { ascending: true })
        .range(from, from + pageSize - 1);
      if (error) throw error;
      const rows = (data ?? []) as typeof out;
      out.push(...rows);
      if (rows.length < pageSize) break;
      from += pageSize;
    }
    return out;
  }

  /** List invoices carrying stored pending_changes (for SYNC-M5 conflict
   *  re-classification). Paginated to bypass the 1000-row PostgREST default.
   *  Sync-support read (mirrors getByExternalIds / listDirtyFakturowniaInvoices) —
   *  NOT a reconcile method. Optional `invoiceId` narrows to a single invoice — the
   *  per-invoice deep-refresh path's scoped variant of the batch reclassify pass. */
  async getInvoicesWithPendingChanges(invoiceId?: number): Promise<InvoiceRow[]> {
    const out: InvoiceRow[] = [];
    const pageSize = 1000;
    let from = 0;
    while (true) {
      let query = this.sb
        .from('invoices')
        .select('*')
        .not('pending_changes', 'is', null);
      if (invoiceId != null) query = query.eq('id', invoiceId);
      const { data, error } = await query
        .order('id', { ascending: true })
        .range(from, from + pageSize - 1);
      if (error) throw error;
      const rows = (data ?? []) as InvoiceRow[];
      out.push(...rows);
      if (rows.length < pageSize) break;
      from += pageSize;
    }
    return out;
  }

  /** List invoices with fakturownia_dirty=true for a direction (outbound push preview).
   *
   *  review_status + fakturownia_approval_status feed the outbound approval push
   *  (the loop cannot map what it cannot see). */
  async listDirtyFakturowniaInvoices(
    direction: InvoiceDirection,
  ): Promise<OutboundInvoiceRow[]> {
    const { data, error } = await this.sb
      .from('invoices')
      .select(OUTBOUND_INVOICE_COLUMNS)
      .eq('fakturownia_dirty', true)
      .eq('direction', direction)
      .not('external_system_id', 'is', null)
      .order('id', { ascending: true });
    if (error) throw error;
    return (data ?? []) as OutboundInvoiceRow[];
  }

  /** Received invoices whose approval MIGHT disagree with Fakturownia, for the
   *  full sweep's drift scan. Returns the raw pair (review_status + mirrored
   *  fakturownia_approval_status) — the mapping lives in FakturowniaService, so
   *  the caller decides what actually mismatches rather than duplicating that
   *  table in SQL.
   *
   *  PAGED: an unranged select silently stops at PostgREST's ~1000-row default,
   *  which on prod would surface 4 of 385 drifted invoices.
   *
   *  NO approval-specific filter here. It used to carry
   *  `.neq('fakturownia_approval_status','rejected')`, which in SQL also drops
   *  every row whose mirror is NULL — fine for approval drift (those pre-date
   *  the migration that started capturing approval_status) but fatal for routing
   *  drift, which hid 197 of the 1,526 EUR invoices this scan exists to find.
   *  One scan serves both; each caller applies its own exclusion in code. */
  async listOutboundDriftCandidates(): Promise<Pick<InvoiceRow,
    'id' | 'review_status' | 'fakturownia_approval_status'
    | 'currency' | 'fakturownia_department_id' | 'buyer_bank_account' | 'seller_company_id'
  >[]> {
    type Row = Pick<InvoiceRow,
      'id' | 'review_status' | 'fakturownia_approval_status'
      | 'currency' | 'fakturownia_department_id' | 'buyer_bank_account' | 'seller_company_id'
    >;
    const out: Row[] = [];
    const pageSize = 1000;
    for (let from = 0; ; from += pageSize) {
      const { data, error } = await this.sb
        .from('invoices')
        .select('id, review_status, fakturownia_approval_status, currency, fakturownia_department_id, buyer_bank_account, seller_company_id')
        .eq('direction', 'received')
        .eq('is_deleted', false)
        .not('external_system_id', 'is', null)
        .order('id', { ascending: true })
        .range(from, from + pageSize - 1);
      if (error) throw error;
      const rows = (data ?? []) as Row[];
      out.push(...rows);
      if (rows.length < pageSize) break;
    }
    return out;
  }

  /** Resolve Fakturownia external ids to invoice ids — drift candidates found in
   *  a table keyed on external_id (the shadow's PENDING-note scan). Chunked so a
   *  long id list cannot blow the URL length or the row cap. */
  async listInvoiceIdsByExternalIds(externalIds: string[]): Promise<number[]> {
    const out: number[] = [];
    const chunk = 200;
    for (let i = 0; i < externalIds.length; i += chunk) {
      const { data, error } = await this.sb
        .from('invoices')
        .select('id')
        .eq('is_deleted', false)
        .in('external_system_id', externalIds.slice(i, i + chunk));
      if (error) throw error;
      out.push(...((data ?? []) as { id: number }[]).map(r => r.id));
    }
    return out;
  }

  /** Flag invoices for the outbound push. This is what makes the full sweep
   *  CONVERGE: drift is discovered by comparing state, but draining runs off the
   *  dirty flag, which each pass clears — so the queue strictly shrinks instead
   *  of the scan re-selecting rows the previous pass already fixed (the mirror it
   *  compares against is only refreshed by the inbound step). */
  async markDirtyByIds(ids: number[]): Promise<number> {
    let marked = 0;
    const chunk = 200;
    for (let i = 0; i < ids.length; i += chunk) {
      const slice = ids.slice(i, i + chunk);
      const { error } = await this.sb
        .from('invoices')
        .update({ fakturownia_dirty: true })
        .in('id', slice);
      if (error) throw error;
      marked += slice.length;
    }
    return marked;
  }

  /** Bring `invoices.notes` in line with a hold decision made in BULK.
   *
   *  updateStatusBulk cannot do this: the marker is a per-row edit of existing
   *  text, and its field whitelist carries neither `notes` nor
   *  `fakturownia_dirty`. Without this, a hold applied by the sweep never got the
   *  marker and never entered the outbound queue — in prod, 519 invoices sat
   *  on_hold with ZERO of them marked locally, so Fakturownia never showed the
   *  hold at all.
   *
   *  Only rows whose note actually changes are written, so re-running is free and
   *  a steady-state sweep flags nothing. */
  async syncHoldMarkers(holdIds: number[], releaseIds: number[]): Promise<number> {
    const ids = [...new Set([...holdIds, ...releaseIds])];
    if (ids.length === 0) return 0;

    const held = new Set(holdIds);
    const rows: { id: number; notes: string | null }[] = [];
    const chunk = 200;
    for (let i = 0; i < ids.length; i += chunk) {
      const { data, error } = await this.sb
        .from('invoices')
        .select('id, notes')
        .in('id', ids.slice(i, i + chunk));
      if (error) throw error;
      rows.push(...((data ?? []) as { id: number; notes: string | null }[]));
    }

    let written = 0;
    for (const row of rows) {
      const next = held.has(row.id) ? withPendingMarker(row.notes) : withoutPendingMarker(row.notes);
      if (next === row.notes) continue;
      const { error } = await this.sb
        .from('invoices')
        .update({ notes: next, fakturownia_dirty: true })
        .eq('id', row.id);
      if (error) throw error;
      written++;
    }
    return written;
  }

  /** Persist what Fakturownia actually holds for the routing fields.
   *
   *  Without this the mirrors only ever move when the inbound projection runs,
   *  which needs a checksum change, which needs the FA payload to change — so an
   *  invoice that is ALREADY correct is flagged, fetched, found fine, written
   *  nothing, and flagged again on the next sweep, forever (~1,196 prod rows).
   *  Writing the observed values closes that loop. */
  async updateFakturowniaRoutingMirror(
    invoiceId: number,
    fields: { fakturownia_department_id?: string | null; buyer_bank_account?: string | null },
  ): Promise<void> {
    if (Object.keys(fields).length === 0) return;
    const { error } = await this.sb
      .from('invoices')
      .update(fields)
      .eq('id', invoiceId);
    if (error) throw error;
  }

  /** Count line items for an invoice (for backfill check). */
  async countLineItems(invoiceId: number): Promise<number> {
    const { count, error } = await this.sb
      .from('invoice_line_items')
      .select('id', { count: 'exact', head: true })
      .eq('invoice_id', invoiceId);
    if (error) throw error;
    return count ?? 0;
  }

  // --- Sync metadata ---

  /** Get last sync timestamp for a sync type (e.g. 'invoice_sync_received'). */
  async getLastSyncAt(syncType: string): Promise<string | null> {
    const { data, error } = await this.sb
      .from('sync_metadata')
      .select('last_sync_at')
      .eq('sync_type', syncType)
      .maybeSingle();
    if (error) throw error;
    return (data as { last_sync_at: string | null } | null)?.last_sync_at ?? null;
  }

  /** Set last sync timestamp for a sync type. */
  async setLastSyncAt(syncType: string, date: string): Promise<void> {
    const { error } = await this.sb
      .from('sync_metadata')
      .upsert(
        { sync_type: syncType, last_sync_at: date, updated_at: new Date().toISOString() },
        { onConflict: 'sync_type' },
      );
    if (error) throw error;
  }

  // --- Sync locking ---

  // B2/A10 — owner-token leases (mirrors InvoiceReconcileService). The old
  // delete-by-type acquire stole any lock >2 min old and released by lock_type
  // regardless of owner, so a >2 min sync would have its lease stolen mid-run and a
  // stale run could wipe a fresh owner's lease. Now try_sync_lock/release_sync_lock
  // are token-scoped with a 20-min TTL that STRICTLY exceeds the 900s Cloud Run
  // ceiling (15 min would only equal it — a max-length run would be stealable at the
  // boundary).
  private readonly syncLockTokens = new Map<string, string>();
  private static readonly SYNC_LOCK_TTL_MINUTES = 20;

  /** Try to acquire a sync lock. Returns true if acquired, false if a live (within-TTL)
   *  lease is held by another owner. Mints an owner token used by releaseSyncLock. */
  async acquireSyncLock(lockType: string): Promise<boolean> {
    const token = crypto.randomUUID();
    // `sb` is an untyped SupabaseClient, so rpc().data is `any` — assert the SQL return type.
    const res = await this.sb.rpc('try_sync_lock', {
      p_lock_type: lockType,
      p_token: token,
      p_ttl_minutes: InvoiceDao.SYNC_LOCK_TTL_MINUTES,
    });
    const data = res.data as boolean | null;
    if (res.error) throw res.error;
    if (data) this.syncLockTokens.set(lockType, token);
    return !!data;
  }

  /** Release a sync lock — only if WE hold it (token-scoped). */
  async releaseSyncLock(lockType: string): Promise<void> {
    const token = this.syncLockTokens.get(lockType) ?? null;
    const { error } = await this.sb.rpc('release_sync_lock', {
      p_lock_type: lockType,
      p_token: token,
    });
    if (error) throw error;
    this.syncLockTokens.delete(lockType);
  }

}

// ── Bank-account correction types (R2) ────────────────────────────────────

export interface InvoiceBankAccountAssignRow {
  id: number;
  direction: InvoiceDirection;
  seller_bank_account: string | null;
  seller_company_id: number;
  external_system_id: string | null;
  /** Both payment views (bank-owned local status + Fakturownia's) — a REPLACEMENT
   *  of an already-stated account is refused once either says money moved. */
  payment_status?: string | null;
  fakturownia_payment_status?: string | null;
}

// ── Payment hold types ──────────────────────────────────────────────────

// `ReceivedHoldCheckRow` lives with the pure hold policy (holdPolicy.ts) so the
// command tier can build the same row shape; re-exported for existing importers.
export type { ReceivedHoldCheckRow };
